import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import pluralize from "pluralize";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import { withTranslation } from "react-i18next";
import { useFlag } from "@unleash/proxy-client-react";

import { Container } from "../../atoms/Container";
import StyledSideTray from "../../atoms/SideTray/StyledSideTray";

import cross from "../../../assets/images/icons/cross.svg";
import backArrow from "../../../assets/images/icons/arrow-left-straight.svg";
import { colors, fonts } from "../../../styles/variables";
import badgeDefault from "../../../assets/images/icons/badge-default.svg";
import cellarBottleImg from "../../../assets/images/temp/bottle-xl-vertical.png";
import { Textarea } from "../../atoms/Input";
import Button from "../../atoms/Button";
import Title from "../../atoms/Title";
import { CREATE_EXTERNAL_ACTION } from "../../../graphql/mutations";
import { GET_MEMBER_LAYOUT_INFO } from "../../../graphql/queries";
import { logError } from "../../../helpers/logging";
import { getWineBaseColor } from "../../../helpers/tools";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import breakpoints from "../../../styles/breakpoints";
import { safeAreaTopPaddingPlus } from "../../../styles/safeArea";
import { GET_AUTH } from "../../../graphql/resolvers/auth";
import { FETCH_POLICY_CACHE_ONLY, I18N_NAMESPACES, UNLEASH_TOPUP_V2 } from "../../../helpers/constants";
import { trackingEvents, useTrackEvent } from "../../../helpers/Analytics";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import TextareaGhostDiv from "../../atoms/TextareaGhostDiv";
import TopUpTray from "../../organisms/TopUpTray";
import BuyAgainTray from "../../organisms/BuyAgainTray";
import BadgeRelatedWines from "../BadgeRelatedWines";
import WineDetailsTray from "../../organisms/WineDetailsTray";

const Cross = styled.img.attrs((props) => ({
  src: props.isBackArrow ? backArrow : cross,
}))`
  height: 15px;
  width: 15px;
  position: absolute;
  left: 0;
  cursor: pointer;
  margin: 0 15px;
`;

const BadgeItemImageHolder = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background-color: ${(props) => (props.isEarned ? colors.blue : colors.silverSoft)};
  margin: auto;
`;

const BadgeItemImage = styled.img`
  max-width: 100%;
  ${(props) =>
    !props.isEarned &&
    css`
      filter: grayscale(100%);
    `}
  zoom: 2.5;
`;

const Header = styled.div`
  text-align: center;
  position: relative;
  ${safeAreaTopPaddingPlus(20)}
  padding-right: 30px;
  padding-bottom: 20px;
  padding-left: 30px;
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

const Points = styled.p`
  font-family: ${fonts.fontInterstateBlackCompressed};
  font-size: 50px;
  line-height: 1em;
  text-align: center;
  margin: 25px 0px;
`;

const Description = styled.p`
  text-align: center;
  white-space: pre-wrap;
  font-size: 18px;
  line-height: 24px;
  ${breakpoints.smDown} {
    font-size: 16px;
    line-height: 22px;
  }
`;

const RequestContainer = styled.div`
  border: 1px solid black;
  margin: 25px 5px;
  padding: 20px;
`;

const StyledTextArea = styled(Textarea)`
  margin-top: 0px;
  margin-bottom: 20px;
`;

const RequestTitle = styled.h5`
  line-height: 1em !important;
  font-size: 20px;
  color: ${colors.black};
  font-family: ${fonts.fontInterstateUltraBlack};
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 20px !important;
`;

const OuterContainer = styled.div`
  width: 100%;
  padding: ${(props) => (props.shouldShowBadgeRelatedWines ? "0" : "0 0 80px 0")};
`;

const BadgeRelatedWinesContainer = styled.div`
  margin-top: 40px;
`;

const Enrol = styled.div`
  padding: 5px;
`;

class BadgeInfoTray extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      note: "",
      height: null,
      isSubmitInProgress: false,
      selectedWineSlug: null,
      selectedTopUpWine: null,
      isWineDetailsTrayOpen: false,
    };

    // eslint-disable-next-line no-underscore-dangle
    this._isMounted = true;
  }

  componentDidMount() {
    // eslint-disable-next-line no-underscore-dangle
    this._isMounted = true;
  }

  componentWillUnmount() {
    // eslint-disable-next-line no-underscore-dangle
    this._isMounted = false;
  }

  /**
   * OnChange for setting a new rating note. We update the state to later use it on the mutation.
   *
   * @param event
   */
  setNewNote = (event) => {
    const { value } = event.target;
    this.setState({ note: value });
  };

  /**
   * this function gets the height of the invisible
   * ..ghost div with text input content, and sets that height in the state,
   * ..so, it can be used for the varying height for Textarea component.
   *
   * */
  setFilledTextareaHeight = () => {
    // eslint-disable-next-line no-underscore-dangle
    if (this._isMounted) {
      const element = this.ghostDiv;
      this.setState({
        height: element.clientHeight,
      });
    }
  };

  /**
   * Calls the mutation to request badge.
   * */
  submitRequestBadge = () => {
    const {
      createExternalActionMutation,
      badge,
      authQuery,
      refetchBadges,
      callbackAfterSubmitRequestBadge,
      onClose,
    } = this.props;

    const { note } = this.state;
    const { id } = badge || {};
    const { auth } = authQuery || {};
    const { memberId } = auth || {};

    this.setState({ isSubmitInProgress: true });

    createExternalActionMutation({
      variables: {
        input: {
          memberId,
          badgeId: id,
          description: note,
        },
      },
      refetchQueries: [{ query: GET_MEMBER_LAYOUT_INFO }],
      awaitRefetchQueries: true,
    })
      .then(({ data }) => {
        const { createExternalAction } = data || {};
        const { errors } = createExternalAction || {};

        if (errors) {
          const errorMessage = errors && errors[0].messages[0];
          logError(errorMessage);
          showErrorAlert(errorMessage || "Sorry, something went wrong!", {
            autoClose: 10000,
          });
        } else {
          // CASE: no-error
          refetchBadges();
          showSuccessAlert("Nice job! Your badge and points will soon be on their way.");
        }

        callbackAfterSubmitRequestBadge();
        this.setState({ isSubmitInProgress: false });
        onClose();
      })
      .catch((error) => {
        logError(error);
        showErrorAlert("Sorry, something went wrong. Please try again.", {
          autoClose: 5000,
        });
        this.setState({ isSubmitInProgress: false });
      });
  };

  /**
   * Toggles the wine details tray visibility.
   *
   * @param {string} wineSlug
   * */
  toggleWineDetailsTray = (wineSlug) => {
    this.setState((previousState) => ({
      isWineDetailsTrayOpen: !previousState.isWineDetailsTrayOpen,
      selectedWineSlug: wineSlug,
    }));
  };

  /**
   * Renders the wine top-up tray for a selected past wine.
   *
   * @returns {Component}
   * */
  renderTopUpTray = () => {
    const { selectedTopUpWine } = this.state;

    const baseColor = getWineBaseColor(selectedTopUpWine?.wineType?.wineClass?.id ?? 0);
    const bottleImage = selectedTopUpWine?.product?.productPhotos?.[0]?.photoSmall || cellarBottleImg;

    return (
      <TopUpTray
        isOffCanvasTopUpOpened
        handleOffCanvasTopUpChange={() => this.setState({ selectedTopUpWine: null, selectedWineSlug: null })}
        wine={selectedTopUpWine}
        baseColor={baseColor}
        bottleImage={bottleImage}
      />
    );
  };

  /**
   * Renders the wine buy again tray for a selected past wine.
   *
   * @returns {Component}
   * */
  renderBuyAgainTray = () => {
    const { selectedTopUpWine } = this.state;

    const baseColor = getWineBaseColor(selectedTopUpWine?.wineType?.wineClass?.id ?? 0);
    const bottleImage = selectedTopUpWine?.product?.productPhotos?.[0]?.photoSmall || cellarBottleImg;
    const wineSlug = selectedTopUpWine?.product?.slug;

    return (
      <BuyAgainTray
        handleOffCanvasBuyAgainChange={() =>
          this.setState({ selectedTopUpWine: null, selectedWineSlug: null })
        }
        wineSlug={wineSlug}
        baseColor={baseColor}
        bottleImage={bottleImage}
      />
    );
  };

  /**
   * Tracks event when members click on button to sign up for wine 101 course.
   * */
  trackEventOnWineCourseClick = () => {
    const { trackEvent } = this.props;
    trackEvent(trackingEvents.TRACKING_EVENT_CLICKED_WINE_101_COURSE_SIGNUP, {});
  };

  /**
   * Render external CTA button
   *
   * @returns {Component}
   * */
  renderExternalCta = () => {
    const { externalCtaText, externalCtaLink } = this.props;

    return (
      <Enrol>
        <Button
          full
          lg
          href={externalCtaLink}
          target="_blank"
          as="a"
          onClick={() => this.trackEventOnWineCourseClick()}
        >
          {externalCtaText}
        </Button>
      </Enrol>
    );
  };

  render() {
    const {
      badge,
      onClose,
      authQuery,
      isBackArrow,
      memberShoppingCartQuery,
      shouldShowExternalCta,
      shouldHideRequestForm,
      isInBoxAndCartTray,
      isBadgeQueryLoading,
      isTopupV2Enabled,
    } = this.props;

    const { note, height, isSubmitInProgress, selectedWineSlug, selectedTopUpWine } = this.state;

    if (authQuery.loading) {
      return <StyledLoading />;
    }

    const {
      externalActionQuestion,
      isEarned,
      level,
      name,
      points,
      shortDescription,
      imageSmallUrl,
      hasWineCharacteristicBadgeRule,
      shouldHideWineRecommendation,
    } = badge || {};

    const shouldShowBadgeRelatedWines = hasWineCharacteristicBadgeRule && !shouldHideWineRecommendation;

    const showRequestForm = externalActionQuestion && !isEarned && !shouldHideRequestForm;

    return (
      <StyledSideTray
        isOpened={!!badge}
        color={colors.white}
        onClickBackdrop={onClose}
        shouldAnimate
        isInBoxAndCartTray={isInBoxAndCartTray}
      >
        {isBadgeQueryLoading && <StyledLoading />}
        {badge && (
          <OuterContainer shouldShowBadgeRelatedWines={shouldShowBadgeRelatedWines}>
            <Header>
              <Cross onClick={onClose} isBackArrow={isBackArrow} />
              <Title tag={5} mb0 uppercase tightLineHeight>
                {`${name}`}
                {!!level && ` (level ${level})`}
              </Title>
            </Header>
            <Container>
              <BadgeItemImageHolder isEarned={isEarned}>
                <BadgeItemImage isEarned={isEarned} src={imageSmallUrl || badgeDefault} />
              </BadgeItemImageHolder>
              <Points>{points > 0 ? pluralize("point", points, true) : "wine level"}</Points>
              <Description>{shortDescription}</Description>
              {shouldShowExternalCta && this.renderExternalCta()}
              {showRequestForm && (
                <RequestContainer>
                  <RequestTitle>{"REQUEST YOUR BADGE"}</RequestTitle>
                  <Description>{externalActionQuestion}</Description>

                  <>
                    <StyledTextArea
                      name="note"
                      placeholder="Type your answer here."
                      onChange={this.setNewNote}
                      value={note}
                      onKeyUp={this.setFilledTextareaHeight}
                      isResizableText
                      customHeight={`${height}px`}
                    />
                    <TextareaGhostDiv
                      ref={(domObj) => {
                        this.ghostDiv = domObj;
                      }}
                      aria-hidden="true"
                    >
                      {note !== null ? note : ""}
                    </TextareaGhostDiv>
                  </>
                  <Button
                    full
                    lg
                    onClick={() => this.submitRequestBadge()}
                    disabled={isSubmitInProgress || note === ""}
                  >
                    {isSubmitInProgress ? "Submitting..." : "Submit"}
                  </Button>
                </RequestContainer>
              )}
            </Container>

            {shouldShowBadgeRelatedWines && (
              <BadgeRelatedWinesContainer>
                <BadgeRelatedWines
                  shouldConcealWines={false}
                  memberShoppingCartQuery={memberShoppingCartQuery}
                  badge={badge}
                  onWineDetailsClick={this.toggleWineDetailsTray}
                  onTopUpClick={(wine) => this.setState({ selectedTopUpWine: wine })}
                  memberChildBadgesFromChallengeBadge={[]}
                />
              </BadgeRelatedWinesContainer>
            )}
          </OuterContainer>
        )}

        {!isTopupV2Enabled && selectedTopUpWine && this.renderTopUpTray()}
        {isTopupV2Enabled && selectedTopUpWine && this.renderBuyAgainTray()}

        {selectedWineSlug && !selectedTopUpWine && (
          <WineDetailsTray
            handleToggleBottleInfoTray={this.toggleWineDetailsTray}
            memberShoppingCartQuery={memberShoppingCartQuery}
            slug={selectedWineSlug}
            isInBoxAndCartTray={isInBoxAndCartTray}
          />
        )}
      </StyledSideTray>
    );
  }
}

BadgeInfoTray.propTypes = {
  badge: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    isEarned: PropTypes.bool,
    level: PropTypes.number,
    imageSmallUrl: PropTypes.string,
    points: PropTypes.number.isRequired,
    shortDescription: PropTypes.string.isRequired,
    externalActionQuestion: PropTypes.string,
    shouldHideWineRecommendation: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
  createExternalActionMutation: PropTypes.func.isRequired,
  authQuery: PropTypes.shape({}).isRequired,
  refetchBadges: PropTypes.func,
  callbackAfterSubmitRequestBadge: PropTypes.func,
  t: PropTypes.func.isRequired,
  isTopupV2Enabled: PropTypes.bool,
  isBackArrow: PropTypes.bool,
  shouldShowExternalCta: PropTypes.bool,
  shouldHideRequestForm: PropTypes.bool,
  isInBoxAndCartTray: PropTypes.bool,
  isBadgeQueryLoading: PropTypes.bool,
  trackEvent: PropTypes.func.isRequired,
};

BadgeInfoTray.defaultProps = {
  badge: null,
  callbackAfterSubmitRequestBadge: () => {},
  refetchBadges: () => {},
  isBackArrow: false,
  shouldShowExternalCta: false,
  shouldHideRequestForm: false,
  isInBoxAndCartTray: false,
  isBadgeQueryLoading: false,
};

const BadgeInfoTrayWrapper = (props) => {
  const isTopupV2Enabled = useFlag(UNLEASH_TOPUP_V2);
  const trackEvent = useTrackEvent();

  return <BadgeInfoTray {...props} isTopupV2Enabled={isTopupV2Enabled} trackEvent={trackEvent} />;
};

export default compose(
  withTranslation([I18N_NAMESPACES.MY_BADGES]),
  graphql(CREATE_EXTERNAL_ACTION, {
    name: "createExternalActionMutation",
  }),
  graphql(GET_AUTH, {
    name: "authQuery",
    options: { fetchPolicy: FETCH_POLICY_CACHE_ONLY },
  })
)(BadgeInfoTrayWrapper);
